import React, { useState, useEffect, useRef } from 'react';
import ProductCard from './ProductCard';
import ProductQuickView from './ProductQuickView';
import LoadingSpinner from '../common/LoadingSpinner';
import { ChevronDown } from 'lucide-react';

const ProductGrid = ({ products, isLoading = false }) => {
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const loaderRef = useRef(null);
  const productsPerPage = 12;

  useEffect(() => {
    // Reset when products change
    setDisplayedProducts(products.slice(0, productsPerPage));
    setPage(1);
    setHasMore(products.length > productsPerPage);
  }, [products]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, isLoading, page]);

  const loadMore = () => {
    const nextPage = page + 1;
    const startIndex = page * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    const newProducts = products.slice(startIndex, endIndex);

    if (newProducts.length > 0) {
      setTimeout(() => {
        setDisplayedProducts(prev => [...prev, ...newProducts]);
        setPage(nextPage);
        setHasMore(endIndex < products.length);
      }, 500);
    } else {
      setHasMore(false);
    }
  };

  const handleQuickView = (product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  if (isLoading && displayedProducts.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="mb-4">
          <svg
            className="w-24 h-24 mx-auto text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
        </div>
        <h3 className="text-2xl font-display font-bold text-dark-800 mb-2">
          No products found
        </h3>
        <p className="text-gray-600">
          Try adjusting your filters or search terms
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {displayedProducts.map((product, index) => (
          <div
            key={product.id}
            style={{ animationDelay: `${(index % productsPerPage) * 0.05}s` }}
          >
            <ProductCard 
              product={product} 
              onQuickView={handleQuickView}
            />
          </div>
        ))}
      </div>

      {/* Infinite Scroll Loader */}
      {hasMore && (
        <div ref={loaderRef} className="flex items-center justify-center py-12">
          <div className="text-center">
            <LoadingSpinner />
            <p className="text-sm text-gray-600 mt-4">Loading more products...</p>
          </div>
        </div>
      )}

      {/* End of Products */}
      {!hasMore && displayedProducts.length > 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 font-medium">
            You've reached the end of the catalog
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all duration-300 transform hover:scale-105"
          >
            <ChevronDown className="w-5 h-5 rotate-180" />
            Back to Top
          </button>
        </div>
      )}

      {/* Quick View Modal */}
      <ProductQuickView
        product={selectedProduct}
        isOpen={isQuickViewOpen}
        onClose={() => setIsQuickViewOpen(false)}
      />
    </>
  );
};

export default ProductGrid;
